import axiosInstance from "core/api/axiosInstance";
import axios from "axios";

// ================================================
// CONFIG
// ================================================
const CONFIG = {
  TIMEOUTS: {
    DEFAULT: 15000,
    HEAVY_QUERY: 60000,
  },
  MAX_RETRIES: 3,
  RETRY_DELAY: 1000,
};

// URLs base
const API_URL = process.env.REACT_APP_API_URL;
const DISPERSION_API_URL = process.env.REACT_APP_DISPERSION_API_URL;

// ================================================
// HELPERS DE AUTENTICACIÓN
// ================================================
const getAuthInfo = () => {
  try {
    const token = localStorage.getItem("token");
    const user = localStorage.getItem("user");
    return {
      hasToken: !!token,
      token: token || null,
      hasUser: !!user,
      userData: user ? JSON.parse(user) : null,
    };
  } catch (error) {
    console.error("Error obteniendo información de autenticación:", error);
    return { hasToken: false, token: null, hasUser: false, userData: null };
  }
};

const validateAuth = () => {
  const authInfo = getAuthInfo();
  if (!authInfo.hasToken) {
    throw new Error("No hay token de autenticación. Por favor inicie sesión.");
  }
  return authInfo;
};

const createHeaders = (additionalHeaders = {}) => {
  const authInfo = getAuthInfo();
  return {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${authInfo.token}`,
    ...additionalHeaders,
  };
};

// ================================================
// HELPERS DE ERRORES Y RESPUESTAS
// ================================================
const handleServiceError = (error, serviceName) => {
  console.error(`Error en ${serviceName}:`, error);

  if (
    error.code === "ERR_CANCELED" ||
    error.name === "CanceledError" ||
    error.name === "AbortError"
  ) {
    return {
      error: true,
      message: "Petición cancelada",
      details: { canceled: true },
      shouldLogout: false,
      isCanceled: true,
      isTimeout: false,
    };
  }

  let errorMessage = `Error en ${serviceName}`;
  let errorDetails = {};

  if (error.response) {
    const backendMessage = error.response.data?.message || error.response.data?.msm || "";
    const backendError = error.response.data?.error || "";

    errorMessage =
      backendMessage ||
      backendError ||
      `Error ${error.response.status}: ${error.response.statusText || "Error desconocido"}`;

    errorDetails = {
      status: error.response.status,
      statusText: error.response.statusText,
      data: error.response.data,
      headers: error.response.headers,
    };

    if (error.response.status === 401) {
      errorMessage =
        backendMessage || "No autorizado. Su sesión ha expirado o el token es inválido.";
    } else if (error.response.status === 403) {
      errorMessage =
        backendMessage || "Acceso denegado. No tiene permisos para realizar esta acción.";
    } else if (error.response.status === 404) {
      errorMessage = backendMessage || "Recurso no encontrado.";
    } else if (error.response.status >= 500) {
      errorMessage = backendMessage || "Error interno del servidor.";
    }
  } else if (error.request) {
    errorMessage =
      "La solicitud está tomando más tiempo de lo esperado. El procesamiento podría continuar en segundo plano.";
    errorDetails = { request: error.request, isTimeout: true };
  } else {
    errorMessage = error.message || "Error de configuración de la solicitud";
    errorDetails = { message: error.message, stack: error.stack };
  }

  return {
    error: true,
    message: errorMessage,
    details: errorDetails,
    shouldLogout: false,
    isCanceled: false,
    isTimeout: error.code === "ECONNABORTED" || error.message?.includes("timeout"),
  };
};

const createResponse = (success, data, message, extra = {}) => ({
  success,
  data: data || [],
  count: Array.isArray(data) ? data.length : 0,
  message: message || (success ? "Operación exitosa" : "Error en la operación"),
  timestamp: new Date().toISOString(),
  ...extra,
});

const withRetry = async (fn, retries = CONFIG.MAX_RETRIES, delay = CONFIG.RETRY_DELAY) => {
  try {
    return await fn();
  } catch (error) {
    if (
      error.code === "ERR_CANCELED" ||
      error.name === "CanceledError" ||
      error.name === "AbortError"
    ) {
      throw error;
    }

    const status = error.response?.status;

    if (
      retries === 0 ||
      status === 401 ||
      status === 403 ||
      status === 404 ||
      (status >= 400 && status < 500)
    ) {
      throw error;
    }

    console.log(`Reintentando petición... Quedan ${retries} intentos`);
    await new Promise((resolve) => setTimeout(resolve, delay));
    return withRetry(fn, retries - 1, delay);
  }
};

// ================================================
// HELPERS DE NORMALIZACIÓN
// ================================================
/**
 * Limpia el nombre del peaje eliminando el prefijo "PEAJE " si existe.
 * Ejemplo: "PEAJE UNISABANA" -> "UNISABANA"
 */
const limpiarNombrePeaje = (peaje) => {
  if (!peaje) return peaje;
  return String(peaje)
    .replace(/^PEAJE\s+/i, "")
    .trim();
};

// ================================================
// SERVICIOS
// ================================================

/**
 * Obtiene todas las concesiones de tipo PEAJE.
 * Endpoint: {API_URL}/api/commerce/get-all
 */
export const getConcesionesPeajeService = async () => {
  try {
    validateAuth();

    const config = {
      headers: createHeaders(),
      timeout: CONFIG.TIMEOUTS.DEFAULT,
    };

    const response = await withRetry(() =>
      axiosInstance.get(`${API_URL}/api/commerce/get-all`, config)
    );

    if (!response.data) {
      throw new Error("No se recibió respuesta del servidor");
    }

    if (!Array.isArray(response.data)) {
      throw new Error("Formato de respuesta inválido");
    }

    const concesiones = response.data
      .filter((item) => item.type === "PEAJE")
      .map((item) => ({
        id: item.id,
        nombre: item.name,
        codigooperador: item.rules?.codigooperador || null,
        organizacion: item.organization,
        estaciones: item.rules?.estaciones_peajes || [],
      }));

    return createResponse(true, concesiones, "Concesiones obtenidas exitosamente");
  } catch (error) {
    const errorResult = handleServiceError(error, "getConcesionesPeajeService");
    return createResponse(false, [], errorResult.message, {
      error: errorResult.message,
      details: errorResult.details,
      isCanceled: errorResult.isCanceled,
    });
  }
};

/**
 * Obtiene los peajes de una concesión por código de operador.
 * Endpoint: {API_URL}/api/conciliation/get-peajes/{codigooperador}
 */
export const getPeajesPorConcesionService = async (codigooperador) => {
  try {
    if (!codigooperador && codigooperador !== 0) {
      throw new Error("El código del operador es requerido");
    }

    validateAuth();

    const codeParam = encodeURIComponent(String(codigooperador).trim());
    const config = {
      headers: createHeaders(),
      timeout: CONFIG.TIMEOUTS.DEFAULT,
    };

    const response = await withRetry(() =>
      axiosInstance.get(`${API_URL}/api/conciliation/get-peajes/${codeParam}`, config)
    );

    if (!response.data) {
      throw new Error("No se recibió respuesta del servidor");
    }

    if (response.data.code !== undefined && response.data.code !== 0) {
      throw new Error(response.data.message || `Error del servidor: código ${response.data.code}`);
    }

    const raw = response.data.data;
    const peajesRaw = Array.isArray(raw) ? raw : raw?.peajes || raw?.data || [];

    const peajes = peajesRaw
      .map((item) => ({
        peaje: item.peaje ?? item.nombre ?? "",
        peajeLimpio: limpiarNombrePeaje(item.peaje ?? item.nombre ?? ""),
        hora_inicio: item.hora_inicio ?? null,
        hora_fin: item.hora_fin ?? null,
        pago_independiente: item.pago_independiente ?? null,
      }))
      .filter((p) => p.peaje);

    return createResponse(true, peajes, "Peajes obtenidos exitosamente", {
      code: response.data.code,
      total: peajes.length,
    });
  } catch (error) {
    const errorResult = handleServiceError(error, "getPeajesPorConcesionService");
    return createResponse(false, [], errorResult.message, {
      error: errorResult.message,
      details: errorResult.details,
      isCanceled: errorResult.isCanceled,
    });
  }
};

/**
 * Obtiene el cuadre de peajes y conciliación.
 * IMPORTANTE: usa el cliente axios directo con base en DISPERSION_API_URL.
 * Endpoint: {DISPERSION_API_URL}/api/py/dispersion/cuadre-peajes
 */
export const getCuadrePeajesService = async ({ concesion, peaje, fecha_inicio, fecha_fin }) => {
  try {
    if (!concesion) {
      throw new Error("La concesión es requerida");
    }

    validateAuth();

    const config = {
      headers: createHeaders(),
      timeout: CONFIG.TIMEOUTS.HEAVY_QUERY,
      // Serializer personalizado: usa %20 en lugar de + para los espacios
      paramsSerializer: (params) => {
        return Object.entries(params)
          .filter(([, value]) => value !== undefined && value !== null && value !== "")
          .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
          .join("&");
      },
    };

    const params = {
      concesion,
      peaje: peaje ? limpiarNombrePeaje(peaje) : undefined,
      fecha_inicio,
      fecha_fin,
    };

    const response = await axios.get(`${API_URL}/api/py/dispersion/cuadre-peajes`, {
      ...config,
      params,
    });

    if (!response.data) {
      throw new Error("No se recibió respuesta del servidor");
    }

    const { data } = response;

    if (data?.code !== 0) {
      throw new Error(data?.message || "Error en la consulta");
    }

    return createResponse(true, data.data || [], data.message || "Consulta exitosa", {
      code: data.code,
      resumenDiario: data.resumen_diario || [],
      totales: data.totales || {},
      peajesConcesion: data.peajes_concesion || [],
    });
  } catch (error) {
    const errorResult = handleServiceError(error, "getCuadrePeajesService");
    return createResponse(false, [], errorResult.message, {
      error: errorResult.message,
      details: errorResult.details,
      isCanceled: errorResult.isCanceled,
      resumenDiario: [],
      totales: {},
      peajesConcesion: [],
    });
  }
};

// ================================================
// EXPORT DEFAULT
// ================================================
export default {
  getConcesionesPeajeService,
  getPeajesPorConcesionService,
  getCuadrePeajesService,
};

// Las observaciones usan el mismo proxy de dispersión que la consulta del cuadre.
export const guardarObservacionPeajeService = async (values, signal) => {
  const auth = validateAuth();
  const profile = auth.userData?.user || auth.userData;
  const userId = profile?.username || profile?.email || profile?.id;
  const payload = {
    concesion: String(values.concesion || "").trim(),
    fecha: String(values.fecha || "").trim(),
    peaje: String(values.peaje || "").trim(),
    observacion: String(values.observacion || "").trim(),
    user_id: userId == null ? "" : String(userId).trim(),
  };
  if (!payload.concesion || !payload.peaje || !/^\d{4}-\d{2}-\d{2}$/.test(payload.fecha)) {
    throw new Error("La fila debe tener concesión, fecha y peaje para guardar la observación.");
  }
  if (!payload.observacion) throw new Error("Escribe una observación antes de guardar.");
  if (!payload.user_id)
    throw new Error("No se identificó el usuario de la sesión. Vuelve a iniciar sesión.");
  const url = `${API_URL}/api/py/dispersion/cuadre-peajes/observaciones`;
  // Una sola petición: nunca se reintenta automáticamente una escritura.
  const response = await axios.post(url, payload, {
    headers: createHeaders(),
    timeout: 30000,
    signal,
  });
  const body = response.data;
  if (!body || body.success === false || (body.code != null && body.code !== 0)) {
    throw new Error(body?.message || "El servidor no confirmó el guardado de la observación.");
  }
  return body;
};
